import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ShareNetwork, Link, Copy, Trash, Eye } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const DashboardSharing = ({ canWrite }) => {
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const fetchLinks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/share/links`, { withCredentials: true });
      setLinks(data);
    } catch (err) { console.error(err.message);
      // silent
    }
  }, []);

  useEffect(() => { if (open) fetchLinks(); }, [open, fetchLinks]);

  const createLink = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/share/create`, {}, { withCredentials: true });
      setLinks(prev => [{ ...data, active: true, created_by_email: 'you', user_id: '' }, ...prev]);
      toast.success('Share link created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const revokeLink = async (token) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/share/${token}`, { withCredentials: true });
      setLinks(prev => prev.filter(l => l.token !== token));
      toast.success('Link revoked');
    } catch (err) { console.error(err.message);
      toast.error('Failed to revoke link');
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const openShared = (token) => {
    window.open(`/shared/${token}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="share-dashboard-btn">
          <ShareNetwork size={16} weight="regular" className="mr-2" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-tight flex items-center gap-2">
            <ShareNetwork size={20} weight="regular" />
            Dashboard Sharing
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">Generate read-only public links to share your dashboard with others.</div>

          {canWrite && (
            <Button onClick={createLink} disabled={creating} className="w-full rounded-sm" data-testid="create-share-link-btn">
              <Link size={16} weight="bold" className="mr-2" />
              {creating ? 'Creating...' : 'Generate New Link'}
            </Button>
          )}

          {links.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No active share links</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {links.map((link) => (
                <div key={link.token} data-testid={`share-link-${link.token.slice(0,8)}`} className="flex items-center gap-2 bg-muted/50 border border-border rounded-sm px-3 py-2">
                  <Link size={14} weight="regular" className="text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono truncate">{`${window.location.origin}/shared/${link.token.slice(0,16)}...`}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Created {new Date(link.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openShared(link.token)} className="p-1.5 hover:bg-muted rounded-sm" title="Preview" data-testid={`preview-link-${link.token.slice(0,8)}`}>
                      <Eye size={14} weight="regular" />
                    </button>
                    <button onClick={() => copyLink(link.token)} className="p-1.5 hover:bg-muted rounded-sm" title="Copy link" data-testid={`copy-link-${link.token.slice(0,8)}`}>
                      <Copy size={14} weight="regular" />
                    </button>
                    {canWrite && (
                      <button onClick={() => revokeLink(link.token)} className="p-1.5 hover:bg-destructive/10 text-destructive rounded-sm" title="Revoke" data-testid={`revoke-link-${link.token.slice(0,8)}`}>
                        <Trash size={14} weight="regular" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardSharing;
